import React, { useState } from 'react';
import { StatusSelect, LocationSelect, BrandSelect, JNSelect, TextEdit, DealSelect, KeyIcon } from './EditableCell';
import { formatDate, dayOfWeek, todayISO, COLOUR_MAP, canEditField } from '../helpers';
import { api } from '../api';
import { showToast } from './Toast';

export function rowClass(v, allVehicles) {
  if (v.delivery_date && String(v.delivery_date).slice(0, 10) === todayISO()) return 'row-today';
  // alternate by date
  const dates = [...new Set(allVehicles.map(x => x.delivery_date && String(x.delivery_date).slice(0, 10)))].filter(Boolean).sort();
  const idx = dates.indexOf(v.delivery_date && String(v.delivery_date).slice(0, 10));
  return idx % 2 === 0 ? 'row-even' : 'row-odd';
}

export function DateCell({ vehicle }) {
  const isToday = vehicle.delivery_date && String(vehicle.delivery_date).slice(0, 10) === todayISO();
  if (!vehicle.delivery_date) return <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>;
  return (
    <>
      <div className="dc-time" style={isToday ? { color: 'var(--amber)', fontWeight: 800 } : null}>
        {vehicle.delivery_time || '—'}
      </div>
      <div className="dc-date">
        {formatDate(vehicle.delivery_date)} · {dayOfWeek(vehicle.delivery_date)}
      </div>
      {isToday && <span className="today-pill">TODAY</span>}
    </>
  );
}

export default function VehicleRow({ vehicle, allVehicles, onUpdate, onChanged, role }) {
  const v = vehicle;
  // Optimistic local copy so UI updates immediately while the API call is in flight
  const [local, setLocal] = useState(v);
  // Keep local in sync when the parent re-fetches
  React.useEffect(() => { setLocal(v); }, [v]);

  // Helper: is this field editable by the current role?
  const can = (field) => canEditField(role, field);

  async function save(field, value) {
    if (!can(field)) {
      showToast('You do not have permission to edit this field');
      return;
    }
    const prev = local[field];
    setLocal((s) => ({ ...s, [field]: value }));
    try {
      await api.updateVehicle(v.id, { [field]: value });
      onChanged?.();
    } catch (e) {
      // revert on failure
      setLocal((s) => ({ ...s, [field]: prev }));
      showToast('Failed: ' + e.message);
    }
  }

  // For Detail/Tint, clicking on the row opens the modal (read-mostly view); for others it's a quick shortcut
  const rowOpensModal = !can('brand'); // restricted roles → click anywhere opens modal as fallback

  // Keys location: at the workshop until "Car Gone" (james=2); at the
  // dealership once "Car Here" (noah>=1).
  const keysAtWorkshop   = (local.james_status ?? 0) < 2;
  const keysAtDealership = (local.noah_status ?? 0) >= 1;

  const isToday = local.delivery_date && String(local.delivery_date).slice(0, 10) === todayISO();

  return (
    <tr className={isToday ? 'row-today' : ''}>
      <td onClick={() => onUpdate(v.id)} style={{ cursor: 'pointer' }} title="Click to open full editor">
        <div className="vm">{local.model}</div>
        <div className="vs">{local.stock_number}{local.ro_number ? ' · ' + local.ro_number : ''}</div>
      </td>
      <td><DealSelect value={local.deal_status} onChange={(val) => save('deal_status', val)} disabled={!can('deal_status')} /></td>
      <td><BrandSelect    value={local.brand}    onChange={(val) => save('brand', val)}    disabled={!can('brand')} /></td>
      <td><LocationSelect value={local.location} onChange={(val) => save('location', val)} disabled={!can('location')} /></td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {local.colour && (
            <span
              className="cdot"
              style={{ background: COLOUR_MAP[local.colour] || '#ccc' }}
            />
          )}
          <TextEdit
            value={local.colour}
            placeholder="Colour"
            onChange={(val) => save('colour', val)}
            disabled={!can('colour')}
          />
        </span>
      </td>
      <td onClick={() => onUpdate(v.id)} style={{ cursor: 'pointer' }} title="Click to edit delivery date/time">
        <DateCell vehicle={local} />
      </td>
      <td><StatusSelect value={local.status_workshop} onChange={(val) => save('status_workshop', val)} disabled={!can('status_workshop')} /></td>
      <td><StatusSelect value={local.status_tint}     onChange={(val) => save('status_tint',     val)} disabled={!can('status_tint')} /></td>
      <td><StatusSelect value={local.status_detail}   onChange={(val) => save('status_detail',   val)} disabled={!can('status_detail')} /></td>
      <td><StatusSelect value={local.status_paint}    onChange={(val) => save('status_paint',    val)} disabled={!can('status_paint')} /></td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <KeyIcon active={keysAtWorkshop} title={keysAtWorkshop ? 'Keys at the Workshop' : 'Keys left the Workshop'} />
          <JNSelect value={local.james_status} type="james" onChange={(val) => save('james_status', val)} disabled={!can('james_status')} />
        </span>
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <KeyIcon active={keysAtDealership} title={keysAtDealership ? 'Keys at the Dealership' : 'Keys not at the Dealership yet'} />
          <JNSelect value={local.noah_status}  type="noah"  onChange={(val) => save('noah_status',  val)} disabled={!can('noah_status')}  />
        </span>
      </td>
      <td style={{ maxWidth: 180, fontSize: 11, color: 'var(--muted)' }}>
        <div className="note-clip" title={local.notes || ''}>
          <TextEdit
            value={local.notes}
            placeholder="Add notes…"
            onChange={(val) => save('notes', val)}
            disabled={!can('notes')}
          />
        </div>
      </td>
      <td><button className="ab p" onClick={() => onUpdate(v.id)}>{rowOpensModal ? 'View' : 'Update'}</button></td>
    </tr>
  );
}
